import React, { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft, PaperPlaneRight, Tag, FileText, CheckCircle, XCircle, Handshake, CalendarCheck, EnvelopeSimple,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function ContractDialog({ rfp, lastOffer }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    negotiated_rate: lastOffer || rfp.nightly_budget || 0,
    start_date: rfp.check_in,
    end_date: rfp.check_out,
    terms: "Rate locked for the room block. Complimentary cancellation up to 72h prior. Weekly housekeeping included.",
  });

  const mut = useMutation({
    mutationFn: (body) => api.createContract(body),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Contract sent", { description: "The company can now review and accept." });
      setOpen(false);
    },
    onError: () => toast.error("Could not create contract"),
  });

  const F = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-contract-dialog-btn"
          className="bg-[#002FA7] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center gap-2">
          <FileText size={16} weight="bold" /> Draft Contract
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Digital Contract</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">{rfp.hotel_name} × {rfp.company_name}</p>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Rate/night</span>
            <input data-testid="contract-rate" type="number" value={form.negotiated_rate} onChange={F("negotiated_rate")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Start</span>
            <input data-testid="contract-start" type="date" value={form.start_date} onChange={F("start_date")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">End</span>
            <input data-testid="contract-end" type="date" value={form.end_date} onChange={F("end_date")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
        </div>
        <label className="text-sm">
          <span className="overline text-[#4B5563] block mb-1">Terms</span>
          <textarea data-testid="contract-terms" rows={4} value={form.terms} onChange={F("terms")}
            className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </label>
        <div className="bg-[#E0E7FF] text-[#002FA7] px-4 py-2.5 text-sm font-semibold flex justify-between">
          <span>{rfp.room_nights} room-nights</span>
          <span>Total ≈ {money(rfp.room_nights * Number(form.negotiated_rate))}</span>
        </div>
        <DialogFooter>
          <button data-testid="send-contract-btn" onClick={() => mut.mutate({
            rfp_id: rfp.id, negotiated_rate: Number(form.negotiated_rate),
            room_nights: rfp.room_nights, start_date: form.start_date, end_date: form.end_date, terms: form.terms,
          })} disabled={mut.isPending}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Sending…" : "Send Contract"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractCard({ c, role }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [emailOpen, setEmailOpen] = useState(false);
  const [emForm, setEmForm] = useState({ to_email: "", message: "" });
  const setStatus = useMutation({
    mutationFn: (status) => api.setContractStatus(c.id, status),
    onSuccess: (_, status) => {
      qc.invalidateQueries();
      toast.success(`Contract ${status}`);
    },
  });
  const emailIt = useMutation({
    mutationFn: () => api.emailContract(c.id, { ...emForm, link_base: window.location.origin }),
    onSuccess: (res) => {
      if (res.sent) toast.success(`Contract emailed to ${res.delivered_to}`);
      else if (!res.email_configured) toast.error("Email isn't set up yet on the server.");
      else toast.error("Couldn't deliver (sending is in test mode until your domain is verified).");
      setEmailOpen(false);
    },
    onError: () => toast.error("Could not send the contract"),
  });

  return (
    <div className="border border-[#111827] bg-white p-5" data-testid={`contract-card-${c.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake size={20} weight="duotone" color="#002FA7" />
          <span className="font-display font-bold text-lg text-[#111827]">Contract</span>
        </div>
        <StatusBadge status={c.status} testid={`contract-status-${c.id}`} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
        <div><span className="overline text-[#4B5563] block">Rate/nt</span><b className="font-mono">{money(c.negotiated_rate)}</b></div>
        <div><span className="overline text-[#4B5563] block">Room-nights</span><b className="font-mono">{c.room_nights}</b></div>
        <div><span className="overline text-[#4B5563] block">Est. total</span><b className="font-mono">{money(c.negotiated_rate * c.room_nights)}</b></div>
      </div>
      <div className="text-sm text-[#4B5563] mt-2">{dateFmt(c.start_date)} → {dateFmt(c.end_date)}</div>
      <p className="text-sm text-[#4B5563] mt-3 bg-[#F3F4F6] p-3">{c.terms}</p>

      {role === "company" && c.status === "sent" && (
        <div className="flex gap-2 mt-4">
          <button data-testid={`accept-contract-${c.id}`} onClick={() => setStatus.mutate("accepted")}
            className="flex-1 bg-[#047857] text-white py-2.5 text-sm font-semibold hover:bg-[#065f46] transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            <CheckCircle size={16} weight="bold" /> Accept & Sign
          </button>
          <button data-testid={`decline-contract-${c.id}`} onClick={() => setStatus.mutate("declined")}
            className="flex-1 border border-[#DC2626] text-[#DC2626] py-2.5 text-sm font-semibold hover:bg-[#DC2626] hover:text-white transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            <XCircle size={16} weight="bold" /> Decline
          </button>
        </div>
      )}
      {role === "company" && c.status === "accepted" && (
        <button data-testid={`book-from-contract-${c.id}`} onClick={() => navigate("/company?tab=bookings")}
          className="w-full mt-4 bg-[#002FA7] text-white py-2.5 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
          <CalendarCheck size={16} weight="bold" /> Go to Booking
        </button>
      )}
      {role === "hotel" && (
        <>
          <button data-testid={`email-contract-${c.id}`} onClick={() => { setEmForm({ to_email: "", message: "" }); setEmailOpen(true); }}
            className="w-full mt-4 border border-[#002FA7] text-[#002FA7] py-2.5 text-sm font-semibold hover:bg-[#E0E7FF] transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            <EnvelopeSimple size={16} weight="bold" /> Email contract to client
          </button>
          <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
            <DialogContent className="rounded-none border-[#111827] max-w-lg" data-testid="email-contract-dialog">
              <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><EnvelopeSimple size={22} weight="bold" color="#002FA7" /> Email contract</DialogTitle></DialogHeader>
              <p className="text-sm text-[#4B5563] -mt-2">Sends a branded PDF of this rate agreement to your client. Replies come straight back to your inbox.</p>
              <div className="space-y-3">
                <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Client email</span>
                  <input data-testid="email-contract-to" type="email" value={emForm.to_email} onChange={(e) => setEmForm((s) => ({ ...s, to_email: e.target.value }))}
                    placeholder={`Defaults to ${c.company_name || "the company"} on file`}
                    className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
                <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Message (optional)</span>
                  <textarea data-testid="email-contract-message" rows={3} value={emForm.message} onChange={(e) => setEmForm((s) => ({ ...s, message: e.target.value }))}
                    placeholder="Thanks for your business — the signed agreement is attached." className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
              </div>
              <DialogFooter>
                <button data-testid="email-contract-send" disabled={emailIt.isPending} onClick={() => emailIt.mutate()}
                  className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60 inline-flex items-center gap-2">
                  <EnvelopeSimple size={16} weight="bold" /> {emailIt.isPending ? "Sending…" : "Send contract"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default function NegotiationRoom() {
  const { rfpId } = useParams();
  const { user } = useAuth();
  const role = user?.role === "company" ? "company" : "hotel";
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [offer, setOffer] = useState("");
  const endRef = useRef(null);

  const { data: rfp } = useQuery({ queryKey: ["rfp", rfpId], queryFn: () => api.rfp(rfpId) });
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", rfpId], queryFn: () => api.messages(rfpId), refetchInterval: 4000,
  });
  const { data: contracts = [] } = useQuery({
    queryKey: ["contracts", "rfp", rfpId],
    queryFn: () => api.contracts(),
    select: (all) => all.filter((c) => c.rfp_id === rfpId),
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = useMutation({
    mutationFn: (body) => api.sendMessage(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", rfpId] });
      qc.invalidateQueries({ queryKey: ["rfp", rfpId] });
      setText(""); setOffer("");
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim() && !offer) return;
    send.mutate({ rfp_id: rfpId, sender_role: role, text: text.trim(), offer_rate: offer ? Number(offer) : null });
  };

  const lastOffer = [...messages].reverse().find((m) => m.offer_rate)?.offer_rate;

  if (!rfp) return <div className="max-w-6xl mx-auto px-8 py-20 text-[#4B5563]">Loading negotiation…</div>;

  const backTo = role === "company" ? "/company" : "/hotel";

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      <Link to={backTo} data-testid="back-to-dash" className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#002FA7] transition-colors">
        <ArrowLeft size={16} weight="bold" /> Back to dashboard
      </Link>

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        {/* RFP summary */}
        <div className="lg:col-span-4 space-y-4">
          <div className="border border-[#111827] p-6">
            <div className="flex items-center justify-between">
              <span className="overline text-[#4B5563]">RFP</span>
              <StatusBadge status={rfp.status} testid="rfp-status" />
            </div>
            <h1 className="font-display font-bold text-2xl text-[#111827] mt-2">{rfp.hotel_name}</h1>
            <p className="text-sm text-[#4B5563]">{rfp.city}</p>
            <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
              <div><span className="overline text-[#4B5563] block">Check-in</span><b>{dateFmt(rfp.check_in)}</b></div>
              <div><span className="overline text-[#4B5563] block">Check-out</span><b>{dateFmt(rfp.check_out)}</b></div>
              <div><span className="overline text-[#4B5563] block">Rooms</span><b className="font-mono">{rfp.num_rooms}</b></div>
              <div><span className="overline text-[#4B5563] block">Room-nights</span><b className="font-mono">{rfp.room_nights}</b></div>
              <div><span className="overline text-[#4B5563] block">Target rate</span><b className="font-mono">{money(rfp.nightly_budget)}</b></div>
              <div><span className="overline text-[#4B5563] block">Company</span><b className="text-xs">{rfp.company_name}</b></div>
            </div>
            {rfp.notes && <p className="text-sm text-[#4B5563] mt-4 bg-[#F3F4F6] p-3">{rfp.notes}</p>}
          </div>

          {contracts.map((c) => <ContractCard key={c.id} c={c} role={role} />)}

          {role === "hotel" && rfp.status !== "contracted" && (
            <div className="border border-[#E5E7EB] p-4">
              <p className="text-sm text-[#4B5563] mb-3">Ready to close? Send a digital contract with the agreed rate.</p>
              <ContractDialog rfp={rfp} lastOffer={lastOffer} />
            </div>
          )}
        </div>

        {/* chat thread */}
        <div className="lg:col-span-8 border border-[#E5E7EB] flex flex-col h-[560px]">
          <div className="glass-header px-5 py-3 flex items-center gap-2">
            <Handshake size={18} weight="bold" color="#002FA7" />
            <span className="font-semibold text-[#111827]">Negotiation thread</span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FBFBFC]" data-testid="message-thread">
            {messages.length === 0 && (
              <p className="text-center text-sm text-[#9CA3AF] mt-10">No messages yet. Open with an offer.</p>
            )}
            {messages.map((m) => {
              const mine = m.sender_role === role;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                    <span className="overline text-[#9CA3AF] mb-1">{m.sender_name}</span>
                    <div className={`px-4 py-2.5 text-sm ${mine ? "bg-[#002FA7] text-white" : "bg-white border border-[#E5E7EB] text-[#111827]"}`}>
                      {m.offer_rate != null && (
                        <div className={`flex items-center gap-1.5 font-mono font-bold mb-1 ${mine ? "text-white" : "text-[#002FA7]"}`}>
                          <Tag size={14} weight="fill" /> Offer: {money(m.offer_rate)}/night
                        </div>
                      )}
                      {m.text}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={submit} className="border-t border-[#E5E7EB] p-3 flex items-center gap-2">
            <div className="relative w-28">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">$</span>
              <input data-testid="offer-input" type="number" value={offer} onChange={(e) => setOffer(e.target.value)}
                placeholder="Offer" className="w-full pl-5 pr-2 py-2.5 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
            <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Type a message…" className="flex-1 px-3 py-2.5 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <button data-testid="send-message-btn" type="submit" disabled={send.isPending}
              className="bg-[#111827] text-white p-3 hover:bg-[#002FA7] transition-colors active:scale-[0.95] disabled:opacity-60">
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
